import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "../assets/css/catalog-product-studio.css";
import NotificationComponent from "../components/Alert/alert";
import CatalogProductCreateWizard from "./catalogProductStudio/CatalogProductCreateWizard";
import ProductStudioEditWorkspace from "./catalogProductStudio/edit/ProductStudioEditWorkspace";
import {
  useCreateCatalogProductDraftMutation,
  useLazyCheckCatalogProductCodeQuery,
  useReplaceCatalogProductMediaMutation,
  useSaveCatalogProductStructureMutation,
  useUploadCatalogMediaAssetMutation,
  useUpdateCatalogProductCoreMutation,
  useUpdateCatalogProductInventoryMutation,
  useUpdateCatalogProductPublishingMutation,
  useUpdateCatalogVariantsMutation,
} from "../api/api";
import {
  useFetchBrandsQuery,
  useFetchColorsQuery,
  useFetchLocationsQuery,
  useFetchTypesQuery,
} from "../api/cmsPanelsApi";
import { useTranslation } from "../hooks/useTranslation";

/**
 * /admin/catalog/products/new → the four-step create wizard (unchanged).
 * /admin/catalog/products/:productId → the Product Studio edit workspace.
 */
const CatalogProductCreateRoute = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  const notificationRef = useRef(null);
  const [createProductFamily, setCreateProductFamily] = useState("shoes");

  const [createCatalogProductDraft, { isLoading: isCreatingDraft }] = useCreateCatalogProductDraftMutation();
  const [checkCatalogProductCode] = useLazyCheckCatalogProductCodeQuery();
  const [uploadCatalogMediaAsset, { isLoading: isUploadingMediaAsset }] = useUploadCatalogMediaAssetMutation();
  const [updateCatalogProductCore, { isLoading: isSavingCore }] = useUpdateCatalogProductCoreMutation();
  const [saveCatalogProductStructure, { isLoading: isSavingStructure }] = useSaveCatalogProductStructureMutation();
  const [updateCatalogVariants, { isLoading: isSavingVariants }] = useUpdateCatalogVariantsMutation();
  const [replaceCatalogProductMedia, { isLoading: isSavingMedia }] = useReplaceCatalogProductMediaMutation();
  const [updateCatalogProductInventory, { isLoading: isSavingInventory }] =
    useUpdateCatalogProductInventoryMutation();
  const [updateCatalogProductPublishing, { isLoading: isSavingPublishing }] =
    useUpdateCatalogProductPublishingMutation();

  const { data: brands = [] } = useFetchBrandsQuery(createProductFamily, { skip: !createProductFamily });
  const { data: colors = [] } = useFetchColorsQuery();
  const { data: merchandisingTypes = [] } = useFetchTypesQuery(createProductFamily, {
    skip: !createProductFamily,
  });
  const { data: locations = [] } = useFetchLocationsQuery();

  const showSuccess = (message) => notificationRef.current?.showNotification(message, "success");
  const showError = (message) => notificationRef.current?.showNotification(message, "danger");

  return (
    <>
      <NotificationComponent ref={notificationRef} />
      <CatalogProductCreateWizard
        t={t}
        brands={brands}
        colors={colors}
        merchandisingTypes={merchandisingTypes}
        locations={locations}
        onProductFamilyChange={setCreateProductFamily}
        createCatalogProductDraft={createCatalogProductDraft}
        checkCatalogProductCode={checkCatalogProductCode}
        uploadCatalogMediaAsset={uploadCatalogMediaAsset}
        updateCatalogProductCore={updateCatalogProductCore}
        saveCatalogProductStructure={saveCatalogProductStructure}
        replaceCatalogProductMedia={replaceCatalogProductMedia}
        isUploadingMediaAsset={isUploadingMediaAsset}
        updateCatalogProductInventory={updateCatalogProductInventory}
        updateCatalogVariants={updateCatalogVariants}
        updateCatalogProductPublishing={updateCatalogProductPublishing}
        isCreatingDraft={isCreatingDraft}
        isSavingCore={isSavingCore}
        isSavingStructure={isSavingStructure}
        isSavingMedia={isSavingMedia}
        isSavingInventory={isSavingInventory}
        isSavingVariants={isSavingVariants}
        isSavingPublishing={isSavingPublishing}
        navigate={navigate}
        showSuccess={showSuccess}
        showError={showError}
      />
    </>
  );
};

const CatalogProductStudio = () => {
  const { productId } = useParams();
  const resolvedProductId = productId ? Number(productId) : null;

  if (!resolvedProductId) {
    return <CatalogProductCreateRoute />;
  }

  // Keyed by id so switching products never carries drafts across.
  return <ProductStudioEditWorkspace key={resolvedProductId} productId={resolvedProductId} />;
};

export default CatalogProductStudio;
